import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import {
  BarChart3, Users, DollarSign, Calendar, HandHelping, FileWarning,
  Loader2, TrendingUp, Home
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ label, value, icon: Icon, color, sub, isDark }) {
  return (
    <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-1">
          <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{value}</p>
        {sub && <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{sub}</p>}
      </CardContent>
    </Card>
  );
}

function BarSegment({ label, value, maxValue, color, isDark }) {
  const pct = maxValue > 0 ? Math.round((value / maxValue) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className={isDark ? 'text-slate-300' : 'text-slate-700'}>{label}</span>
        <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{value}</span>
      </div>
      <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-700' : 'bg-slate-200'}`}>
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(pct, 2)}%` }} />
      </div>
    </div>
  );
}

export default function ReportsPanel({ orgId }) {
  const { isDark } = useTheme();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/org/${orgId}/overview`);
      setReport(data);
    } catch (err) {
      console.error('Failed to fetch report:', err);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  if (!report) {
    return (
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-8 text-center">
          <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Unable to load report</p>
        </CardContent>
      </Card>
    );
  }

  const s = report.summary;
  const maxPlayers = Math.max(...report.teams.map(t => t.player_count), 1);

  return (
    <div className="space-y-6" data-testid="reports-panel">
      {/* Season Banner */}
      {report.season && (
        <Card className={isDark ? 'bg-gradient-to-r from-amber-900/30 to-slate-800/50 border-amber-800/50' : 'bg-gradient-to-r from-amber-50 to-white border-amber-200'}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>{report.org_name}</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Season #{report.season.number} · {report.season.label}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Founded {report.season.founding_year}</p>
            </div>
            <div className="text-right">
              <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{s.total_coaches} Coaches</p>
              {report.birthdays?.upcoming_count > 0 && (
                <Badge className="bg-pink-600 mt-1">{report.birthdays.upcoming_count} upcoming birthdays</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Players" value={s.total_players} icon={Users} color="text-cyan-500" sub={`${s.boys_count}M / ${s.girls_count}F`} isDark={isDark} />
        <StatCard label="Families" value={s.total_families} icon={Home} color="text-blue-500" isDark={isDark} />
        <StatCard label="Revenue" value={`$${s.total_revenue}`} icon={DollarSign} color="text-green-500" sub={s.pending_revenue > 0 ? `$${s.pending_revenue} pending` : null} isDark={isDark} />
        <StatCard label="Teams" value={s.total_teams} icon={Users} color="text-amber-500" isDark={isDark} />
        <StatCard label="Overdue" value={s.overdue_payments || 0} icon={DollarSign} color={s.overdue_payments > 0 ? "text-red-500" : "text-slate-400"} isDark={isDark} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Team Breakdown */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <BarChart3 className="h-4 w-4 text-amber-500" />
              Team Enrollment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {report.teams.map((t) => (
              <BarSegment
                key={t.id}
                label={`${t.name} (${t.code})`}
                value={t.player_count}
                maxValue={maxPlayers}
                color="bg-amber-500"
                isDark={isDark}
              />
            ))}
          </CardContent>
        </Card>

        {/* Revenue by Team */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <TrendingUp className="h-4 w-4 text-green-500" />
              Revenue by Team
            </CardTitle>
          </CardHeader>
          <CardContent>
            {report.teams.map((t) => {
              const teamRev = t.player_count * t.season_cost;
              return (
                <div key={t.id} className={`flex justify-between py-2 border-b last:border-0 ${isDark ? 'border-slate-700' : 'border-slate-100'}`}>
                  <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{t.name}</span>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{t.player_count} x ${t.season_cost}</span>
                    <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>${teamRev}</span>
                  </div>
                </div>
              );
            })}
            <div className={`flex justify-between pt-3 mt-2 border-t ${isDark ? 'border-slate-600' : 'border-slate-200'}`}>
              <span className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>Potential Total</span>
              <span className="font-bold text-green-500">${report.teams.reduce((sum, t) => sum + t.player_count * t.season_cost, 0)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Schedule */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Calendar className="h-4 w-4 text-blue-500" />
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              {[
                { l: 'Total', v: report.schedule.total_games, c: 'text-blue-400' },
                { l: 'Confirmed', v: report.schedule.accepted, c: 'text-green-400' },
                { l: 'Pending', v: report.schedule.pending, c: 'text-amber-400' },
                { l: 'Upcoming', v: report.schedule.upcoming, c: 'text-cyan-400' },
              ].map((item) => (
                <div key={item.l} className="text-center">
                  <p className={`text-xl font-bold ${item.c}`}>{item.v}</p>
                  <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{item.l}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Volunteer */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <HandHelping className="h-4 w-4 text-amber-500" />
              Volunteer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-3">
              <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{report.volunteer.fill_rate}%</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Fill Rate</p>
            </div>
            <div className="flex justify-between text-sm">
              <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{report.volunteer.filled_slots}/{report.volunteer.total_slots} slots</span>
              <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>{report.volunteer.open_tasks} open</span>
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className="pb-2">
            <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <FileWarning className="h-4 w-4 text-red-500" />
              Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-3">
              <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{report.documents.total_tracked}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Total Tracked</p>
            </div>
            {report.documents.expiring_soon > 0 && (
              <Badge className="bg-red-600 w-full justify-center">
                {report.documents.expiring_soon} expiring within 30 days
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payments */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}>
        <CardHeader className="pb-2">
          <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <DollarSign className="h-4 w-4 text-green-500" />
            Payment Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">${s.total_revenue}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Collected</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-400">${s.pending_revenue}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Outstanding</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{s.paid_payments}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Paid</p>
            </div>
            <div className="text-center">
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{s.pending_payments}</p>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Pending</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
        Report generated {new Date(report.generated_at).toLocaleString()} · Season #{report.season?.number}
      </p>
    </div>
  );
}
